// CORE
import { List } from 'immutable';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';

// MUI
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchBox from 'components/SearchBox';
import { makeStyles } from 'tss-react/mui';

// DATA
import type { Account } from 'app-components/flux/accounts';
import { accountsSelectors } from 'app-components/flux/accounts';
import type { Goal } from 'app-components/flux/goals/goalsTypes';
import { goalsActions, goalsSelectors, goalsUtils } from 'app-components/flux/goals';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tagsSelectors } from 'app-components/flux/tags';
import { tracker } from 'app-components/utils/core';
import useTxnActions from 'data/transactionActions/useTxnActions';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { transactionsSelectors, transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';

// Shared
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import QPanelButton from 'components/QPanelButton';
import AmountField from 'components/QuickenControls/AmountField';
import TransactionSelectList from 'components/TransactionSelectList';
import ZeroStateView from 'components/ZeroStateView';

import { Divider } from '@mui/material';
import QIllustration from 'components/QIllustration';

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
  panelButton: {
    textAlign: 'left',
  },
  amountBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1),
    minWidth: 160,
    flexGrow: 1,
  },
  longAmountBox: {
    background: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 232,
    maxWidth: 288,
    flexGrow: 3,
  },
  subAmountBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: `0 ${theme.spacing(1)}`,
  },
  withdrawAmount: {
    textAlign: 'left',
    maxWidth: 212,
  },
  withdrawTypeHeader: {
    marginBottom: theme.spacing(2),
  },
  accountName: {
    maxWidth: 229,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

function TransactionSelection(props: {
  highlight: any;
  onClick: any;
  withdrawMaximum: any;
  handleAccountSelectChange: any;
  withdrawToAccount: any;
  goal: any;
  accountId: string;
}) {
  const { highlight, onClick, withdrawMaximum, handleAccountSelectChange, withdrawToAccount, goal, accountId } = props;
  const [search, setSearch] = useState('');
  const { classes } = useStyles();
  const possibleContributionAccounts = useAppSelector(goalsSelectors.getPossibleContributionAccounts);
  const uniqueContributorBalances = useAppSelector((state) =>
    goalsSelectors.getUniqueContributors(state, { accountId: goal.accountId }),
  );
  const uniqueContributionIds = uniqueContributorBalances.keySeq().toArray();
  const accountsWithContributions = List().withMutations((list) => {
    uniqueContributionIds.forEach((id) =>
      possibleContributionAccounts.get(id) ? list.push(possibleContributionAccounts.get(id)) : null,
    );
  });

  return (
    <Box display='flex' flexDirection='column'>
      <TextField
        value={withdrawToAccount?.id}
        defaultValue={withdrawToAccount?.name}
        select
        id='withdraw-account-select'
        label='Withdraw to'
        InputProps={{
          disableUnderline: accountsWithContributions?.size <= 1 || undefined,
          readOnly: accountsWithContributions?.size <= 1,
        }}
        SelectProps={{
          IconComponent: accountsWithContributions?.size <= 1 ? 'div' : undefined,
        }}
        variant={accountsWithContributions?.size <= 1 ? 'standard' : 'filled'}
        onChange={handleAccountSelectChange}
      >
        {accountsWithContributions.valueSeq?.().map?.((acct: Account) =>
          acct ? (
            <MenuItem key={acct.id ?? acct.clientId} value={acct.id ?? acct.clientId}>
              <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                <div className={classes.accountName}>{acct.name}</div>
                <div>
                  Max available&nbsp;
                  <AmountField editable={false} value={uniqueContributorBalances.get(acct.id || '')} />
                </div>
              </Box>
            </MenuItem>
          ) : (
            <MenuItem key='deleted-acct' value='Deleted account'>
              Deleted account
            </MenuItem>
          ),
        )}
      </TextField>
      <Divider sx={{ my: 2 }} />
      <SearchBox onSearch={setSearch} initialValue={search} autoFocus />
      <TransactionSelectList
        clickFunc={onClick}
        highlight={highlight}
        filter={search}
        transactionListSelector={transactionsSelectors.getPotentialGoalTransactions}
        selectorProps={{ withdrawMaximum, accountId }}
      />
    </Box>
  );
}

function WithdrawFromGoal(props: { goal: Goal }) {
  const { goal } = props;

  const [selectedTxn, setSelectedTxn] = useState<TransactionProps | null>(null);

  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const { saveTransactions } = useTxnActions({
    wrapperId: 'WithdrawFromGoal',
    robotMode: true,
    callback: () => {
      tracker.track(tracker.events.takeFrom);
      dispatch(rootUiActions.removeDialog());
    },
  }); // custom hook for saving txn

  // extract the accounts we could withdraw too
  const allAccounts = useAppSelector(accountsSelectors.getAccountsById);
  const possibleContributionAccounts = useAppSelector(goalsSelectors.getPossibleContributionAccounts);
  const uniqueContributorBalances = useAppSelector((state) =>
    goalsSelectors.getUniqueContributors(state, { accountId: goal.accountId }),
  );
  const goalWithdrawTxnsFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'goalWithdrawTxns'));
  const uniqueContributionIds = uniqueContributorBalances.keySeq().toArray();
  const accountsWithContributions = List().withMutations((list) => {
    uniqueContributionIds.forEach((id) =>
      possibleContributionAccounts.get(id) ? list.push(possibleContributionAccounts.get(id)) : null,
    );
  });

  const goalTag = useAppSelector((state) => tagsSelectors.getTagById(state, goal.tagId ?? ''));

  const [withdrawType, setWithdrawType] = useState<null | string>(null);
  const [withdrawToAccount, setWithdrawToAccount]: any = useState(accountsWithContributions.first());
  const withdrawMaximum = uniqueContributorBalances.get(withdrawToAccount?.id) || 0;
  const [withdrawAmount, setWithdrawAmount] = useState<number | null>(null);
  const [awaitingWithdraw, setAwaitingWithdraw] = useState(false);
  const [validate, setValidate] = useState<boolean>();

  const amountVal = withdrawAmount || (selectedTxn?.amount ?? 0) * -1;
  const amountIsNumber = !Number.isNaN(Number(amountVal));
  const amountError = Boolean(amountVal !== null && (amountVal <= 0 || amountVal > withdrawMaximum));

  const formError = Boolean(!amountIsNumber || !amountVal || !withdrawToAccount || amountError || awaitingWithdraw);

  // Callback functions
  const onBack = useCallback(() => setWithdrawType(null), []);

  const onHandleClose = useCallback(
    (_event?: any, _reason?: string) => {
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch],
  );

  const handleSubmit = useCallback(() => {
    setValidate(true);
    if (!amountVal) {
      setWithdrawAmount(0);
      return;
    }
    if (!formError && withdrawToAccount.id && goal.accountId && amountVal > 0) {
      setAwaitingWithdraw(true);
      // set up subtype
      let subtype: 'GOAL_SPENT' | undefined;
      if (withdrawType === 'spend' || withdrawType === 'spend-txn') {
        subtype = 'GOAL_SPENT';
      }

      const saveArr = [];
      if (withdrawType === 'spend-txn' && selectedTxn) {
        saveArr.push(
          goalsUtils.makeGoalTransferTxn(
            goal,
            goal.accountId,
            withdrawToAccount.id,
            -Math.abs(Number(selectedTxn.amount ?? 0)),
            undefined,
            undefined,
            subtype,
            selectedTxn.postedOn,
          ),
        );
        if (goalTag) {
          saveArr.push(
            transactionsTypes.mkCashFlowTransaction({
              ...selectedTxn,
              tags: Array.isArray(selectedTxn?.tags)
                ? [...selectedTxn.tags.filter((tag: any) => tag !== null), goalTag]
                : [goalTag],
              isExcludedFromF2S: true,
            }),
          );
        }
      } else {
        const transferTxns = goalsUtils.makeGoalTransactions(
          goal,
          withdrawToAccount.id,
          -Math.abs(Number(amountVal)),
          subtype,
        );
        saveArr.push(transferTxns[0]);
        saveArr.push(transferTxns[1]);
      }
      saveTransactions(saveArr);

      setTimeout(() => {
        dispatch(goalsActions.refreshGoalBalance(withdrawToAccount.id));
      }, 4000);
    }
  }, [withdrawToAccount, amountVal, goal, saveTransactions, withdrawType, formError, selectedTxn, goalTag, dispatch]);

  const handleAccountSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWithdrawToAccount(allAccounts.get(e.target.value));
    },
    [allAccounts],
  );

  const handleWithdrawAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setWithdrawAmount(Number(e.target.value));
  }, []);

  // Memoized values
  const dialogTitle = useMemo(() => {
    switch (withdrawType) {
      case 'spend':
        return 'Withdraw to spend';
      case 'spend-txn':
        return 'Spend with a real transaction';
      case 'other':
        return 'Withdraw for something else';
      case null:
      default:
        return 'Withdraw from goal';
    }
  }, [withdrawType]);

  const noMoney = Boolean(
    accountsWithContributions.size < 1 || (goal.savedSoFar && goal.spent && goal.savedSoFar + goal.spent <= 0),
  );

  const dialogContent = () => {
    if (awaitingWithdraw) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
          <CircularProgress size={100} />
        </Box>
      );
    }

    if (noMoney) {
      return (
        <Box padding={1} pb={3} display='flex' justifyContent='center' alignItems='center'>
          <ZeroStateView
            title='No money available'
            description='You don&apos;t have any money saved to withdraw yet.'
            qIllustration='starSad'
          />
        </Box>
      );
    }

    switch (withdrawType) {
      case 'spend-txn':
        return (
          <TransactionSelection
            highlight={selectedTxn?.id ?? ''}
            withdrawMaximum={withdrawMaximum}
            withdrawToAccount={withdrawToAccount}
            handleAccountSelectChange={handleAccountSelectChange}
            onClick={(e: { currentTarget: { id: string } }) => {
              const clickedTxn = JSON.parse(e.currentTarget.id);
              setSelectedTxn(clickedTxn);
            }}
            goal={goal}
            accountId={withdrawToAccount.id}
          />
        );
      case 'spend':
      case 'other':
        return (
          <Box display='flex' gap={1}>
            <QIllustration name={withdrawType === 'spend' ? 'goalSpend' : 'spendWallet'} size='md' />
            <Box display='flex' flexDirection='column' gap={2} mb={1}>
              <Typography variant='titleRegular'>{goal.name}</Typography>

              <Box display='flex' mb={1} minWidth='416px'>
                <div className={classes.longAmountBox}>
                  <div className={classes.subAmountBox}>
                    <Typography variant='paragraphRegular'>Saved so far</Typography>
                    <AmountField variant='titleRegular' editable={false} value={goal.savedSoFar} />
                  </div>

                  {withdrawType === 'spend' && (
                    <div className={classes.subAmountBox}>
                      <Typography variant='paragraphRegular'>Spent so far</Typography>
                      <AmountField variant='titleRegular' editable={false} value={Math.abs(goal.spent || 0)} />
                    </div>
                  )}
                </div>

                <div className={classes.amountBox}>
                  <Typography variant='paragraphRegular'>
                    Available to&nbsp;{withdrawType === 'spend' ? 'spend' : 'withdraw'}
                  </Typography>
                  <AmountField
                    variant='titleRegular'
                    editable={false}
                    value={(goal.savedSoFar || 0) + (goal.spent || 0)}
                  />
                </div>
              </Box>

              <AmountField
                label='Amount to withdraw'
                amountType='amount'
                showAmountAdornment
                currencySymbol='USD'
                textFieldVariant='filled'
                id='amount-to-withdraw'
                editable
                onChange={handleWithdrawAmountChange}
                value={withdrawAmount}
                className={classes.withdrawAmount}
                error={validate && amountError}
                helperText={
                  (validate &&
                    amountError &&
                    (withdrawAmount && withdrawAmount > withdrawMaximum
                      ? 'You cannot withdraw more than you have contributed'
                      : 'Enter an amount to withdraw')) ||
                  null
                }
              />

              <TextField
                value={withdrawToAccount?.id}
                defaultValue={withdrawToAccount?.name}
                select
                id='withdraw-account-select'
                label='Withdraw to'
                InputProps={{
                  disableUnderline: accountsWithContributions?.size <= 1 || undefined,
                  readOnly: accountsWithContributions?.size <= 1,
                }}
                SelectProps={{
                  IconComponent: accountsWithContributions?.size <= 1 ? 'div' : undefined,
                }}
                variant={accountsWithContributions?.size <= 1 ? 'standard' : 'filled'}
                onChange={handleAccountSelectChange}
              >
                {accountsWithContributions.valueSeq?.().map?.((acct: Account) =>
                  acct ? (
                    <MenuItem key={acct.id ?? acct.clientId} value={acct.id ?? acct.clientId}>
                      <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                        <div className={classes.accountName}>{acct.name}</div>
                        <div>
                          Max available&nbsp;
                          <AmountField editable={false} value={uniqueContributorBalances.get(acct.id || '')} />
                        </div>
                      </Box>
                    </MenuItem>
                  ) : (
                    <MenuItem key='deleted-acct' value='Deleted account'>
                      Deleted account
                    </MenuItem>
                  ),
                )}
              </TextField>
            </Box>
          </Box>
        );
      case null:
      default:
        return (
          <Box display='flex' flexDirection='column' mb={4}>
            <Typography className={classes.withdrawTypeHeader} variant='titleRegular'>
              {`Where's the money going?`}
            </Typography>
            {goalWithdrawTxnsFF ? (
              <QPanelButton
                id='spend-on-goal-from-txn'
                onClick={() => setWithdrawType('spend-txn')}
                className={classes.panelButton}
                startIcon={<QIllustration name='goalSpend' size='xs' />}
                title='Tag money towards this goal'
                subtitle={[
                  'Link a transaction you used to spend on this goal.',
                  <br key='break' />,
                  'The total amount saved will not be affected. This transaction will be excluded from your Spending Plan.',
                ]}
              />
            ) : (
              <QPanelButton
                id='withdraw-to-spend-on-goal'
                onClick={() => setWithdrawType('spend')}
                className={classes.panelButton}
                startIcon={<QIllustration name='goalSpend' size='xs' />}
                title='Spend a custom amount'
                subtitle={[
                  'Spend from your available balance.',
                  <br key='break' />,
                  'The total amount saved will not be affected.',
                ]}
              />
            )}
            <QPanelButton
              id='withdraw-to-spend-on-something-else'
              onClick={() => setWithdrawType('other')}
              className={classes.panelButton}
              startIcon={<QIllustration name='spendWallet' size='xs' />}
              title='Withdraw for something else'
              subtitle='Your overall goal progress and amount saved will be reduced.'
            />
          </Box>
        );
    }
  };

  return (
    <StdDialog
      onBack={onBack}
      onClose={onHandleClose}
      open
      sharedcomponentid='WITHDRAW_FROM_GOAL_DIALOG'
      showBackButton={withdrawType}
      showCloseButton
      size='sm'
      title={dialogTitle}
    >
      <DialogContent className={classes.contentContainer}>{dialogContent()}</DialogContent>

      {(noMoney || withdrawType) && (
        <StdDialogActions
          primary={
            noMoney
              ? {
                  children: 'Close',
                  id: 'Close-Dialog-Button',
                  'aria-label': 'Close-Dialog',
                  onClick: onHandleClose,
                }
              : {
                  children: 'Withdraw',
                  onClick: handleSubmit,
                  id: 'submit-withdraw-from-goal',
                  'aria-label': 'withdraw',
                  tooltipText: formError ? 'Form fields need attention' : undefined,
                }
          }
        />
      )}
    </StdDialog>
  );
}

export default WithdrawFromGoal;
